// combinator-modes.ts
// A segmented toggle that shows one mode's panel at a time, outside the core.

import { gsap } from 'gsap';
import Base from '../base/base';
import './combinator-modes.scss';

export interface CombinatorModesOptions {
  selector?: string;
  root?: HTMLElement;
  debug?: boolean;
}

export const MODES_ATTRIBUTE = 'data-cmb-modes';
export const MODE_ATTRIBUTE = 'data-cmb-mode';
export const MODE_BUTTON_ATTRIBUTE = 'data-cmb-mode-button';
export const MODE_EVENT = 'cmb:mode';

export default class CombinatorModes extends Base {
  readonly root: HTMLElement | null;

  private buttons: HTMLElement[] = [];
  private panels: HTMLElement[] = [];
  private slider: HTMLElement | null = null;
  private mode = '';
  private boundOnClick: (event: Event) => void;

  constructor(options: CombinatorModesOptions = {}) {
    super(options.debug || false);

    this.root =
      options.root || document.querySelector<HTMLElement>(options.selector || `[${MODES_ATTRIBUTE}]`);
    this.boundOnClick = this.onClick.bind(this);

    this.init();
  }

  get current(): string {
    return this.mode;
  }

  private init(): void {
    if (!this.root) {
      this.log('CombinatorModes: root not found');
      return;
    }

    this.panels = Array.from(this.root.querySelectorAll<HTMLElement>('[data-cmb]'));
    this.buttons = Array.from(this.root.querySelectorAll<HTMLElement>(`[${MODE_BUTTON_ATTRIBUTE}]`));

    if (!this.buttons.length) this.adoptButtons();

    this.slider = this.root.querySelector<HTMLElement>('.segmented-control__slider');

    const first = this.buttons[0] ? this.buttons[0].getAttribute(MODE_BUTTON_ATTRIBUTE) || '' : '';

    this.root.addEventListener('click', this.boundOnClick);
    this.show(this.root.getAttribute(MODE_ATTRIBUTE) || first, false);
    this.log('CombinatorModes started', this.buttons.length, 'modes');
  }

  /** A bare button takes the mode of the panel at its index. */
  private adoptButtons(): void {
    const bare = Array.from(this.root!.querySelectorAll<HTMLElement>('.segmented-control__button'));

    bare.forEach((button, index) => {
      const panel = this.panels[index];

      if (panel) button.setAttribute(MODE_BUTTON_ATTRIBUTE, panel.getAttribute('data-cmb') || '');
    });

    this.buttons = bare.filter((button) => button.getAttribute(MODE_BUTTON_ATTRIBUTE));
  }

  private onClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const button = target ? target.closest<HTMLElement>(`[${MODE_BUTTON_ATTRIBUTE}]`) : null;

    if (!button || !this.root!.contains(button)) return;

    event.preventDefault();
    this.show(button.getAttribute(MODE_BUTTON_ATTRIBUTE) || '', true);
  }

  show(mode: string, animate: boolean): void {
    if (!mode || mode === this.mode) return;

    const previous = this.mode;

    this.mode = mode;
    this.root!.setAttribute(MODE_ATTRIBUTE, mode);

    this.buttons.forEach((button, index) => {
      const active = button.getAttribute(MODE_BUTTON_ATTRIBUTE) === mode;

      button.classList.toggle('scb--active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      if (active && this.slider) {
        gsap.to(this.slider, { xPercent: index * 100, duration: animate ? 0.35 : 0, ease: 'power3.out' });
      }
    });

    this.panels.forEach((panel) => {
      const active = panel.getAttribute('data-cmb') === mode;
      const was = panel.classList.contains('cmb-mode--active');

      if (active && !was) {
        panel.classList.add('cmb-mode--active');

        if (animate) {
          gsap.fromTo(panel, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power2.out' });
        }
      } else if (!active && was) {
        panel.classList.remove('cmb-mode--active');
        gsap.set(panel, { clearProps: 'opacity,visibility,transform' });
      }
    });

    this.root!.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: { mode, previous }, bubbles: true }));
  }

  destroy(): void {
    if (this.root) this.root.removeEventListener('click', this.boundOnClick);
  }
}
